/**
 * Enhanced Form Submission Utility
 * Provides loading states, button locking, progress indicators, and error handling
 */

class FormSubmissionManager {
    constructor(formId, submitButtonSelector = 'button[type="submit"]') {
        this.form = document.getElementById(formId);
        this.submitButton = this.form?.querySelector(submitButtonSelector);
        this.originalButtonText = this.submitButton?.textContent || 'Submit';
        this.isSubmitting = false;
        
        // Create loading overlay
        this.createLoadingOverlay();
        
        // Bind submit handler
        if (this.form) {
            this.form.addEventListener('submit', (e) => this.handleSubmit(e));
        }
    }

    createLoadingOverlay() {
        // Create overlay container
        this.overlay = document.createElement('div');
        this.overlay.id = 'submission-overlay';
        this.overlay.className = 'fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center hidden';
        
        // Store the original loading HTML for restoration
        this.originalLoadingHTML = `
            <div class="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-2xl">
                <div class="mb-4">
                    <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                </div>
                <h3 class="text-lg font-semibold mb-2" id="loading-title">Processing Submission</h3>
                <p class="text-gray-600 mb-4" id="loading-message">Please wait while we process your form...</p>
                <div class="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div class="bg-blue-600 h-2 rounded-full transition-all duration-300" id="progress-bar" style="width: 0%"></div>
                </div>
                <p class="text-sm text-gray-500" id="progress-text">Initializing...</p>
            </div>
        `;
        
        this.overlay.innerHTML = this.originalLoadingHTML;
        document.body.appendChild(this.overlay);
    }

    showLoading(title = 'Processing Submission', message = 'Please wait while we process your form...') {
        if (this.isSubmitting) return;
        
        this.isSubmitting = true;
        
        // Restore original loading HTML in case it was replaced by success/error/warning
        this.overlay.innerHTML = this.originalLoadingHTML;
        
        // Lock the submit button
        if (this.submitButton) {
            this.submitButton.disabled = true;
            this.submitButton.innerHTML = `
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
            `;
        }

        // Show overlay
        this.overlay.classList.remove('hidden');
        document.getElementById('loading-title').textContent = title;
        document.getElementById('loading-message').textContent = message;
        this.updateProgress(0, 'Initializing...');
        
        // Prevent page scroll
        document.body.style.overflow = 'hidden';
    }

    updateProgress(percentage, text) {
        const progressBar = document.getElementById('progress-bar');
        const progressText = document.getElementById('progress-text');
        
        if (progressBar) {
            progressBar.style.width = `${Math.max(0, Math.min(100, percentage))}%`;
        }
        
        if (progressText) {
            progressText.textContent = text;
        }
    }

    hideLoading() {
        this.isSubmitting = false;
        
        // Reset submit button
        if (this.submitButton) {
            this.submitButton.disabled = false;
            this.submitButton.innerHTML = this.originalButtonText;
        }

        // Hide overlay
        this.overlay.classList.add('hidden');
        
        // Restore page scroll
        document.body.style.overflow = '';
    }

    showSuccess(message = 'Form submitted successfully!', autoRedirect = true, redirectUrl = '/Forms/ThankYou') {
        // Update overlay content for success
        this.overlay.innerHTML = `
            <div class="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-2xl">
                <div class="mb-4">
                    <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                        <svg class="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                        </svg>
                    </div>
                </div>
                <h3 class="text-lg font-semibold mb-2 text-green-800">Success!</h3>
                <p class="text-gray-600 mb-4">${message}</p>
                ${autoRedirect ? '<p class="text-sm text-gray-500">Redirecting to thank you page...</p>' : ''}
            </div>
        `;

        if (autoRedirect) {
            setTimeout(() => {
                window.location.href = redirectUrl;
            }, 2000);
        }
    }

    showError(message = 'An error occurred while processing your form.', allowRetry = true) {
        // Update overlay content for error
        this.overlay.innerHTML = `
            <div class="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-2xl">
                <div class="mb-4">
                    <div class="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
                        <svg class="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                        </svg>
                    </div>
                </div>
                <h3 class="text-lg font-semibold mb-2 text-red-800">Submission Failed</h3>
                <p class="text-gray-600 mb-4">${message}</p>
                <div class="flex gap-3 justify-center">
                    ${allowRetry ? '<button id="retry-btn" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">Try Again</button>' : ''}
                    <button id="close-error-btn" class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors">Close</button>
                </div>
            </div>
        `;

        // Add event listeners for buttons
        const retryBtn = document.getElementById('retry-btn');
        const closeBtn = document.getElementById('close-error-btn');

        if (retryBtn) {
            retryBtn.addEventListener('click', () => {
                this.hideLoading();
            });
        }

        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                this.hideLoading();
            });
        }
    }

    showWarning(message = 'Please review your submission.', allowContinue = true) {
        // Update overlay content for warning
        this.overlay.innerHTML = `
            <div class="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-2xl">
                <div class="mb-4">
                    <div class="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full">
                        <svg class="w-8 h-8 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                        </svg>
                    </div>
                </div>
                <h3 class="text-lg font-semibold mb-2 text-yellow-800">Warning</h3>
                <p class="text-gray-600 mb-4">${message}</p>
                <div class="flex gap-3 justify-center">
                    ${allowContinue ? '<button id="continue-btn" class="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors">Continue</button>' : ''}
                    <button id="close-warning-btn" class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors">Close</button>
                </div>
            </div>
        `;

        // Add event listeners for buttons
        const continueBtn = document.getElementById('continue-btn');
        const closeBtn = document.getElementById('close-warning-btn');

        if (continueBtn) {
            continueBtn.addEventListener('click', () => {
                this.hideLoading();
            });
        }

        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                this.hideLoading();
            });
        }
    }

    async handleSubmit(event) {
        // This method should be overridden by specific form implementations
        event.preventDefault();
        console.warn('handleSubmit method should be overridden by specific form implementations');
    }

    // Utility method for making API requests with progress updates
    async makeRequest(url, data, options = {}) {
        const defaultOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        };

        const requestOptions = { ...defaultOptions, ...options };

        try {
            const response = await fetch(url, requestOptions);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Request failed:', error);
            throw error;
        }
    }

    // Method to validate form before submission
    validateForm() {
        const requiredFields = this.form.querySelectorAll('[required]');
        let isValid = true;
        let firstInvalidField = null;

        requiredFields.forEach(field => {
            if (!field.value.trim()) {
                field.style.borderColor = '#dc2626';
                field.style.backgroundColor = '#fef2f2';
                if (!firstInvalidField) firstInvalidField = field;
                isValid = false;
            } else {
                field.style.borderColor = '#d1d5db';
                field.style.backgroundColor = '#ffffff';
            }
        });

        if (!isValid && firstInvalidField) {
            firstInvalidField.focus();
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return isValid;
    }

    // Clean up method
    destroy() {
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        document.body.style.overflow = '';
    }
}

// Make FormSubmissionManager globally available
window.FormSubmissionManager = FormSubmissionManager;